import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Single-pass scrolling tape. Items are duplicated so the CSS keyframes
 * (translateX 0 → -50%) creates a seamless loop. Pauses on hover (CSS).
 *
 * Props:
 *   articles  — array of news article objects from newsIntelAPI
 *   indices   — array of market index summaries (optional)
 *   onArticleClick(id)
 */
const TopTicker = ({ articles = [], indices = [], onArticleClick }) => {
  const navigate = useNavigate();

  const items = useMemo(() => {
    const indexItems = (indices || []).slice(0, 12).map((idx, i) => ({
      key: `idx-${i}-${idx.code || idx.ts_code || i}`,
      kind: 'index',
      label: idx.name || idx.code || '',
      value: idx.close ?? idx.price ?? '—',
      change: idx.pct_chg ?? idx.change_pct ?? null,
      onClick: idx.code ? () => navigate(`/market/index/${idx.code}`) : undefined,
    }));
    const newsItems = (articles || []).slice(0, 20).map((a) => ({
      key: `news-${a.id}`,
      kind: 'news',
      label: 'NEWS',
      value: a.title || '',
      onClick: () => (onArticleClick ? onArticleClick(a.id) : navigate(`/news-intelligence?article=${a.id}`)),
    }));
    // Interleave indices first, then news
    const mixed = [...indexItems, ...newsItems];
    // Duplicate for the CSS seamless loop
    return [...mixed, ...mixed.map(m => ({ ...m, key: m.key + '-dup' }))];
  }, [articles, indices, navigate, onArticleClick]);

  if (items.length === 0) {
    return (
      <div className="term-ticker">
        <div className="term-ticker-track" style={{ animation: 'none', paddingLeft: 12 }}>
          <span className="term-muted">— waiting for market feed —</span>
        </div>
      </div>
    );
  }

  return (
    <div className="term-ticker">
      <div className="term-ticker-track">
        {items.map(it => {
          const tickClass =
            it.change == null ? '' : it.change > 0 ? 'tick-up' : it.change < 0 ? 'tick-down' : 'tick-flat';
          const arrow = it.change == null ? '' : it.change > 0 ? '▲' : it.change < 0 ? '▼' : '·';
          const pct = it.change == null ? '' : ` ${arrow} ${Number(it.change).toFixed(2)}%`;
          return (
            <span
              key={it.key}
              className="term-ticker-item"
              role={it.onClick ? 'button' : undefined}
              onClick={it.onClick}
              style={{ cursor: it.onClick ? 'pointer' : 'default' }}
            >
              <span className="label">{it.label}</span>
              <span className={tickClass}>{it.value}{pct}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default TopTicker;
